import * as React from 'react'

import { inject, observer } from 'mobx-react'

import { IProjectAddress } from '~/client/graph/types.generated'
import WeatherWeekPresenter, {
  IWeatherWeekForecast,
} from '~/client/src/shared/components/WeatherPresenters/WeatherWeekPresenter'
import WeatherUnits from '~/client/src/shared/enums/WeatherUnits'
import WeatherForecastInfo from '~/client/src/shared/models/WeatherInfo'
import WeatherForecastsStore from '~/client/src/shared/stores/domain/WeatherForecasts.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import './WeatherWeeklyWidget.scss'

interface IProps {
  startDate: number | Date
  startWithDate?: number | Date
  className?: string

  iconSize?: number
  forecasts?: WeatherForecastInfo[]
  weatherForecastsStore?: WeatherForecastsStore
  temperatureUnit: string
  projectDateStore?: ProjectDateStore
  projectAddress: IProjectAddress

  /** Whether it's constrained horizontally */
  compactH?: boolean
  /** Whether it's constrained vertically */
  compactV?: boolean
}

@inject('weatherForecastsStore', 'projectDateStore')
@observer
export default class WeatherWeeklWidget extends React.Component<IProps> {
  public render() {
    const {
      forecasts,
      weatherForecastsStore,
      iconSize,
      className,
      projectDateStore,
      projectAddress,
      compactH,
      compactV,
    } = this.props

    const today = projectDateStore.startOfDay(new Date()).getTime()
    const tomorrow = projectDateStore.addDays(today, 1)
    const fiveDaysFromToday = projectDateStore
      .endOfDay(projectDateStore.addDays(today, 5))
      .getTime()

    // Filter and sort forecasts for the 5-day range (excluding today)
    const sortedForecasts = (forecasts ?? [])
      .sort((a, b) => a.date - b.date)
      .filter(x => x.date > today && x.date <= fiveDaysFromToday)

    if (sortedForecasts.length === 0) {
      return null
    }

    const units =
      weatherForecastsStore.weatherUnits === WeatherUnits.Imperial
        ? 'imperial'
        : 'metric'

    // Transform WeatherForecastInfo[] to presenter format
    const presenterForecasts: IWeatherWeekForecast[] = sortedForecasts.map(
      f => ({
        date: f.date,
        temperature: f.currentTemp,
        weatherIconCode: f.weatherIconCode,
        windSpeed: f.windSpeed,
        windDegree: f.windDegree,
        probabilityOfPrecipitation: f.probabilityOfPrecipitation,
        amountOfPrecipitation: f.amountOfPrecipitation,
        weatherDescription: f.weatherDescription,
        compactV: compactV,
        compactH: compactH,
      }),
    )

    // Format date range
    const formattedDateRange = `${projectDateStore.getMonthAndDayToDisplay(
      tomorrow,
    )} - ${projectDateStore.getMonthDayAndYearToDisplay(fiveDaysFromToday)}`

    return (
      <WeatherWeekPresenter
        forecasts={presenterForecasts}
        projectAddress={projectAddress}
        units={units}
        formattedDateRange={formattedDateRange}
        formatWeekday={this.formatWeekday}
        formatMonthDay={this.formatMonthDay}
        iconSize={iconSize}
        className={className}
        compactH={compactH}
        compactV={compactV}
      />
    )
  }

  private formatWeekday = (timestamp: number): string => {
    return this.props.projectDateStore.getWeekdayToDisplay(timestamp)
  }

  private formatMonthDay = (timestamp: number): string => {
    return this.props.projectDateStore.getMonthAndDayToDisplay(timestamp)
  }
}
